import React from "react";

/**
 * Salva-vidas do code-splitting.
 *
 * O AppRouter carrega ~60 telas com `React.lazy`, ou seja, cada navegação pode
 * buscar um `.chunk.js` novo NO MEIO da sessão. Se esse arquivo não existir
 * mais — o deploy apaga os bundles antigos, e uma aba aberta continua pedindo
 * os hashes da versão anterior — a promessa do `lazy` é rejeitada, o
 * `<Suspense>` nunca resolve e, sem ninguém para capturar, o React desmonta a
 * árvore inteira: o cliente fica olhando o spinner, ou uma tela branca.
 *
 * Aqui isso vira o que de fato é — "saiu uma versão nova" — e se resolve com
 * um recarregamento.
 */

/* Cada navegador descreve essa falha de um jeito. O `name` é o caminho do
   webpack 5; o resto cobre Chrome, Firefox e Safari com import dinâmico. */
const PADROES_DE_CHUNK = [
  /loading chunk \S+ failed/i,
  /loading css chunk \S+ failed/i,
  /failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /importing a module script failed/i,
  /'text\/html' is not a valid javascript mime type/i,
];

export const isChunkLoadError = (error) => {
  if (!error) return false;
  if (error.name === "ChunkLoadError") return true;

  const mensagem = String(error.message || error);
  return PADROES_DE_CHUNK.some((padrao) => padrao.test(mensagem));
};

/* Uma retentativa por sessão. Sem a trava, um chunk que falha por outro motivo
   (o arquivo existe mas a rede cai sempre) viraria um laço de recarregamento —
   que é pior do que o erro. */
const CHAVE_RETENTATIVA = "relinq:chunk-retry";

const jaTentou = () => {
  try {
    return !!sessionStorage.getItem(CHAVE_RETENTATIVA);
  } catch (e) {
    // Sem storage não há trava possível, então não arriscamos o laço.
    return true;
  }
};

const marcarTentativa = () => {
  try {
    sessionStorage.setItem(CHAVE_RETENTATIVA, "1");
  } catch (e) {
    /* modo privado: segue sem marcar */
  }
};

/* URL diferente da atual: um reload comum pode ser servido do mesmo cache que
   entregou o HTML velho. Com o parâmetro, o documento vem do servidor e traz os
   hashes que realmente estão no disco. */
const recarregarIgnorandoCache = () => {
  const { pathname, search, hash } = window.location;
  const separador = search ? "&" : "?";

  window.location.replace(
    `${pathname}${search}${separador}_r=${Date.now()}${hash}`,
  );
};

class ChunkErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error) {
    if (!isChunkLoadError(error) || jaTentou()) return;

    marcarTentativa();
    recarregarIgnorandoCache();
  }

  render() {
    const { error } = this.state;

    if (!error) return this.props.children;

    /* Não é problema de bundle: relançamos para não engolir bug de verdade
       numa tela genérica. Sem outro boundary acima, o comportamento é o mesmo
       de antes desta classe existir. */
    if (!isChunkLoadError(error)) throw error;

    /* É problema de bundle e a retentativa automática já foi gasta (ou o
       recarregamento está a caminho): falar com o usuário em vez de deixar o
       spinner girando. */
    return (
      <div className="flex h-screen w-full items-center justify-center p-6">
        <div className="max-w-sm text-center">
          <h1 className="text-lg font-semibold text-gray-800">
            Uma versão nova do sistema foi publicada
          </h1>
          <p className="mt-2 text-sm text-gray-600">
            Recarregue a página para continuar de onde parou.
          </p>
          <button
            type="button"
            onClick={recarregarIgnorandoCache}
            className="mt-5 rounded-lg bg-blue-600 px-5 py-2 text-sm font-semibold text-white hover:bg-blue-700"
          >
            Recarregar
          </button>
        </div>
      </div>
    );
  }
}

export default ChunkErrorBoundary;
